/* storybook-check-ignore */
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../../components/exclusives/layout/Layout';
import ReduxWrapper from '../../../components/shared/ReduxWrapper';
import { Box } from '@opendoor/bricks/core';
import Hero from '../../../components/exclusives/Hero';
import TourModal from '../../../components/exclusives/TourModal';
import HomeDetails from '../../../components/exclusives/HomeDetails';
import Footer from '../../../components/exclusives/layout/Footer';
import moment from 'moment-timezone';
import { useRouter } from 'next/router';
import { analytics } from '@opendoor/observability';
import {
  ListingWithComputedProperties,
  ListingWithListingStates,
} from '../../../declarations/exclusives/listing';
import { remainingDaysRoundedToEndTime } from '../../../helpers/exclusives/exclusiveListingInfo';
import PropertyGallery from '../../../components/exclusives/PropertyGallery';
import FAQFooter from '../../../components/exclusives/FAQFooter';
import Seo from '../../../components/exclusives/Seo';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import ReservedHomeHeader from '../../../components/exclusives/reserve/ReservedHomeHeader';
import { css, Global } from '@emotion/react';
import { ListingProvider } from '../../../components/exclusives/hooks/useListing';
import HomeSelfTourNotification from '../../../components/exclusives/homeSelfTour/HomeSelfTourNotification';
import useExclusiveAnalytics from '../../../components/exclusives/hooks/useExclusiveAnalytics';
import { getListing } from '../../../components/exclusives/helpers/listing';
import {
  getIsSellerOfferExperiment,
  getExclusivesEventHomeInfo,
  getListingAddressHumanReadable,
  getListingStreet,
  getListingThumbnail,
  getListingWithComputedProperties,
  getListingWithListingStates,
  getIsOOMLS,
  getListingPhotos,
  injectPriorPhotos,
} from '../../../helpers/exclusives/listing';
import ObfuscatedAddressNotification from '../../../components/exclusives/ObfuscatedAddressNotification';
import dynamic from 'next/dynamic';
import { EXCLUSIVE_RAILS_HOST } from '../../../components/globals';
import useSWR from 'swr';
import TouringHeader from '../../../components/exclusives/TouringHeader';
import PriceBreakdown from 'components/exclusives/PriceBreakdown';
import { getMapLink } from 'helpers/exclusives/map';
import { mediaBreakpoints, breakpoints } from 'helpers/exclusives/pdpMediaBreakpoints';
import { getFeatureFlagIsEnabled } from 'components/api';
import { NotificationSectionProvider } from 'components/exclusives/NotificationSection';
import { ListingStates } from 'components/exclusives/constants/constants';

const Spritesheet = dynamic(() => import('@opendoor/bricks/core/Icon/Spritesheet'), {
  ssr: false,
});

type Props = ListingWithListingStates & { isExclusivesRequestATourFFEnabled: boolean };

export async function getServerSideProps(
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<Props>> {
  const { slug } = context.query;
  if (typeof slug != 'string') {
    return {
      notFound: true,
    };
  }

  const listing = await getListing(slug, /*include_expired=*/ true);

  if (!listing) {
    return {
      notFound: true,
    };
  }

  if (await getFeatureFlagIsEnabled('exclusives-prior-photos', { ssrCtx: context })) {
    await injectPriorPhotos([listing]);
  }

  const props = getListingWithListingStates(listing);

  if (props.exclusive_listing_info.state == 'el_setup') {
    if (!props.listing_photos.length) {
      props.listing_photos = getListingPhotos(props);
    }
  }

  return {
    props: {
      ...props,
      isExclusivesRequestATourFFEnabled: await getFeatureFlagIsEnabled(
        'exclusives_request_a_tour',
        { ssrCtx: context },
      ),
    },
  };
}

const globalStyle = css`
  body {
    position: relative;
  }
  @media (max-width: ${mediaBreakpoints.MD}) {
    #front-chat-iframe {
      display: none;
    }
  }
`;

const fetcher = (url: string) =>
  fetch(`${EXCLUSIVE_RAILS_HOST}/${url}`).then((r) =>
    r.json().then((r) => r.map(getListingWithComputedProperties)),
  );

function Page({ isExclusivesRequestATourFFEnabled, ...props }: Props) {
  const router = useRouter();
  const { trackExclusivesEvent } = useExclusiveAnalytics();
  const [isTourModalOpen, setIsTourModalOpen] = useState(false);
  const isSelfTour = router.query.selfTour === 'true';
  const isSellerOfferExperiment = getIsSellerOfferExperiment(props);
  const [headerTooltip, setHeaderTooltip] = useState(isSelfTour);
  const [showSelfTourNotification, setShowSelfTourNotification] = useState(isSelfTour);
  const { data: listings, error: listingsError } = useSWR<ListingWithComputedProperties[]>(
    `api/v1/exclusive_listings`,
    fetcher,
  );
  const tourableListings = useMemo(() => {
    return !isSellerOfferExperiment || !listings
      ? []
      : listings.filter((l) => {
          return (
            l.id !== props.id &&
            getIsSellerOfferExperiment(l) &&
            l.el_state !== ListingStates.EL_IN_CONTRACT
          );
        });
  }, [listings, isSellerOfferExperiment]);
  const listingsFromMarket = useMemo(() => {
    if (!listings) return [];
    return listings.filter((l) => l.address.market.identifier === props.address.market.identifier);
  }, [listings, props]);
  useEffect(() => {
    const { source, market, slug } = router.query;

    if (!source || !isSelfTour || !market) {
      return;
    }

    const payload = {
      source,
      market,
      slug,
      type: props.exclusive_listing_info.state,
      ...getExclusivesEventHomeInfo(props),
    };
    trackExclusivesEvent('exclusives-open-house', 'pdp-viewed', undefined, payload);
  }, [isSelfTour, router.query.source, router.query.market, props.exclusive_listing_info.state]);

  useEffect(() => {
    const isCustomerOwned = getIsSellerOfferExperiment(props);
    if (isCustomerOwned) {
      window.Sprig?.('track', `exclusives-pdp-customer-owned-home`);
    }
    trackExclusivesEvent('exclusives-pdp', 'view', props, {
      market: props.address?.market?.identifier,
      ...getExclusivesEventHomeInfo(props),
    });
  }, []);

  useEffect(() => {
    if (router.query.email) {
      analytics.register({ email: router.query.email });
    }
  }, [router.query.email]);

  const title = `${getListingAddressHumanReadable(props)} | Opendoor Exclusives`;
  const metaImage = getListingThumbnail(props, '_1800x1200');

  const daysRemaining = remainingDaysRoundedToEndTime(props.exclusive_listing_info);

  const exclusivelyListedUntil = moment(props.exclusive_listing_info.start_time)
    .add(13, 'days')
    .format('LL');

  const exclusiveUntilRemarks =
    (daysRemaining ?? 0) > 0 ? ` Only exclusive until ${exclusivelyListedUntil}. ` : ' ';
  const description = `Skip the bidding wars and purchase this ${props.bedrooms} bed, ${
    props.bathrooms
  } bath, ${props.square_footage.toLocaleString(
    'en-US',
  )} sqft home via Opendoor Exclusives at ${getListingAddressHumanReadable(props)} for $${(
    props.listing_price_cents / 100
  ).toLocaleString('en-US')}.${exclusiveUntilRemarks}${props.remarks}`;

  const pdpUrl = `https://www.opendoor.com/exclusives/homes/${props.slug}`;

  const schema1 = JSON.stringify({
    '@type': 'SingleFamilyResidence',
    '@context': 'http://schema.org',
    name: getListingAddressHumanReadable(props),
    floorSize: {
      '@type': 'QuantitativeValue',
      '@context': 'http://schema.org',
      value: `${props.square_footage}`,
    },
    numberOfBedrooms: props.bedrooms,
    numberOfBathroomsTotal: props.bathrooms,
    address: {
      '@type': 'PostalAddress',
      '@context': 'http://schema.org',
      streetAddress: getListingStreet(props),
      addressLocality: props.address.city,
      addressRegion: props.address.state,
      postalCode: props.address.postal_code,
    },
    url: pdpUrl,
  });
  const schema2 = JSON.stringify({
    '@type': 'Offer',
    '@id': `${pdpUrl}#offer`,
    name: getListingAddressHumanReadable(props),
    url: pdpUrl,
    price: props.listing_price_cents / 100,
    priceCurrency: 'USD',
    availability: 'For Sale',
    availabilityEnds: moment(props.exclusive_listing_info.start_time)
      .add(14, 'days')
      .format('YYYY-MM-DD'),
    offeredBy: 'Opendoor',
    seller: 'Opendoor',
    itemOffered: pdpUrl,
  });

  const schema3 = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'Self Tour - 8:00 AM - 8:00 PM',
    startDate: moment(props.exclusive_listing_info.start_time).format('YYYY-MM-DD'),
    endDate: moment(props.exclusive_listing_info.start_time).add(14, 'days').format('YYYY-MM-DD'),
    description: `Self Tour for ${getListingAddressHumanReadable(props)}`,
    url: pdpUrl,
    location: `${getListingAddressHumanReadable(props)}`,
  });

  return (
    <ReduxWrapper>
      <ListingProvider value={props}>
        <React.Fragment>
          <Global styles={globalStyle} />
          <Seo title={title} description={description} image={metaImage}>
            <link
              rel="stylesheet"
              type="text/css"
              href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
            />
            <link
              rel="stylesheet"
              type="text/css"
              href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
            />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schema1 }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schema2 }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schema3 }} />
          </Seo>
          <Spritesheet />
          <Layout
            title={title}
            hideHeaderNavLinks={false}
            logoToGallery={true}
            headerMaxWidth="var(--content-narrow-max-width)"
            listingsToSearch={listings || []}
            openLinkOnNewTab
            isListingsToSearchLoading={!listings && !listingsError}
            breakpoints={breakpoints}
            Header={
              isSelfTour ? (
                <TouringHeader
                  tooltip={headerTooltip}
                  tooltipText="You can self tour any Exclusive home between 8am - 8pm"
                  onTooltipDismiss={() => setHeaderTooltip(false)}
                />
              ) : undefined
            }
          >
            <ReservedHomeHeader listing={props} />
            {showSelfTourNotification && (
              <HomeSelfTourNotification
                listing={props}
                onCloseClick={() => setShowSelfTourNotification(false)}
              />
            )}
            <Box
              css={css`
                --right-side-max-width: 824px;
              `}
              pb={['4rem', '4rem', '4rem', 0]}
            >
              <Box as="main" display={['block', 'block', 'block', 'flex', 'flex']}>
                <PropertyGallery onSelfTourClick={() => setIsTourModalOpen(true)} />
                <Box
                  width={[
                    '100%',
                    '100%',
                    '100%',
                    '53%',
                    'max(var(--right-side-max-width), min(var(--right-side-max-width), 36%))',
                  ]}
                  maxWidth={['unset', null, null, null, 'var(--right-side-max-width)']}
                  boxSizing="border-box"
                >
                  <ObfuscatedAddressNotification
                    isVisible={!!props.exclusive_listing_info.should_obfuscate_address}
                    mt="0.05rem"
                    px={['1.5rem', '1.5rem', '1.5rem', '3.5rem']}
                  />
                  <NotificationSectionProvider>
                    <Hero
                      onTourButtonClick={() => {
                        trackExclusivesEvent('exclusives-pdp', 'self-tour', props, {
                          action: 'cta-click',
                          ...getExclusivesEventHomeInfo(props),
                        });
                        setIsTourModalOpen((prevState) => !prevState);
                      }}
                      listing={props}
                      tourableListings={tourableListings}
                      isExclusivesRequestATourFFEnabled={isExclusivesRequestATourFFEnabled}
                    />
                  </NotificationSectionProvider>
                  <Box
                    maxWidth={['644px', '644px', '644px', '942px']}
                    width={['unset', null, 'calc(100% - 3rem)', 'unset']}
                    ml={['1.5rem', '1.5rem', 'auto', '3.5rem']}
                    mr={['1.5rem', '1.5rem', 'auto', '2rem']}
                  >
                    <HomeDetails listing={props} listings={listingsFromMarket} />
                    <PriceBreakdown listing={props} />
                    <FAQFooter
                      isCustomerOwned={isSellerOfferExperiment}
                      isOOMLS={getIsOOMLS(props)}
                      showOwnedInfo
                      pb="0.5rem"
                      fontWeight="semibold"
                      fontSize="s2"
                      opacity="0.8"
                    />

                    <TourModal
                      isOpen={isTourModalOpen}
                      onRequestClose={() => setIsTourModalOpen((prevState) => !prevState)}
                      address={props.address}
                      mapLink={getMapLink(props)}
                    />
                  </Box>
                </Box>
              </Box>
              <Footer maxWidth="var(--content-narrow-max-width)" />
            </Box>
          </Layout>
        </React.Fragment>
      </ListingProvider>
    </ReduxWrapper>
  );
}

export default Page;
